import HttpService from "@/services/HttpService";
import Apis from "@/services/Apis";

// Default discount rule object
const defaultDiscountRule = {
  name: "",
  description: "",
  discountType: "instant",
  isActive: true,
  priority: 0,
  sourceType: ["pos", "online-store", "kiosk"],
  schedule: {
    startDate: null,
    endDate: null,
    dayOfWeek: [],
    startTime: null,
    endTime: null
  },
  conditions: {
    type: "AND",
    rules: []
  },
  triggers: {
    minQuantity: null,
    minSpend: null,
    customerConditions: {
      isVeteran: null,
      visitCount: null,
      firstPurchase: null
    }
  },
  effects: [],
  stacking: {
    stackingBehavior: "default",
    excludeTypes: [],
    excludeRuleIds: [],
    onlyStackWithTypes: []
  },
  usageLimits: {
    maxPerOrder: null,
    maxPerCustomer: null,
    maxPerCustomerPerDay: null,
    maxTotalUses: null
  },
};

const state = {
  discountRules: [],
  discountRule: { ...defaultDiscountRule },
  loadingStatus: {
    saveDiscountRule: false,
    getDiscountRules: false,
    getDiscountRuleById: false,
    deleteDiscountRule: false,
    previewOrder: false,
    previewDiscount: false,
    resetUsages: false,
  },
  previewResult: null,
  selectedFilter: {
    isActive: true,
    isDeleted: false,
  },
  selectedPreFilters: {
    isActive: true,
    isDeleted: false,
  },
  sort: {
    isActive: -1,
    isDeleted: 1,
  },
  pagination: { skip: 0, limit: 20 },
  initialFilter: {
    isActive: true,
    isDeleted: false,
  },
  filterButtons: [
    {
      isSelected: true,
      _id: 0,
      label: "All",
      name: "all",
      showAll: true,
      filter: {

      },
    },
    {
      isSelected: false,
      _id: 1,
      label: "Coupons",
      name: "coupon",
      showAll: false,
      filter: {
        discountType: "coupon",
      },
    },
    {
      isSelected: false,
      _id: 2,
      label: "Sale Pricings",
      name: "sale-pricing",
      showAll: false,
      filter: {
        discountType: "salePricing",
      },
    },
    {
      isSelected: false,
      _id: 3,
      label: "Bundles",
      name: "bundle",
      showAll: false,
      filter: {
        discountType: "bundle",
      },
    },
  ]
};

const mutations = {
  setLoadingStatus: (state, payload) => {
    if (payload) {
      state.loadingStatus[payload.api] = payload.status;
    }
  },
  setDiscountRules: (state, payload) => {
    state.discountRules = payload;
  },
  setDiscountRule: (state, payload) => {
    if (payload._id === "NEW") {
      state.discountRule = { ...defaultDiscountRule };
    } else {
      state.discountRule = payload;
    }
  },
  setPreviewResult: (state, payload) => {
    state.previewResult = payload;
  },
  setSelectedFilter: (state, payload) => {
    state.selectedFilter = payload;
  },
  setSelectedPreFilters: (state, payload) => {
    state.selectedPreFilters = payload;
  },
  updateSort: (state, payload) => {
    state.sort = payload;
  },
  updatePagination: (state, payload) => {
    state.pagination = payload;
  },
  updateInitialFilter: (state, payload) => {
    state.initialFilter = payload;
  },
  updateFilterButtons: (state, payload) => {
    state.filterButtons = payload;
  },
};

const getters = {
  discountRules: (state) => state.discountRules,
  discountRule: (state) => state.discountRule,
  loadingStatus: (state) => state.loadingStatus,
  previewResult: (state) => state.previewResult,
  selectedFilter: (state) => state.selectedFilter,
  selectedPreFilters: (state) => state.selectedPreFilters,
  sort: (state) => state.sort,
  pagination: (state) => state.pagination,
  initialFilter: (state) => state.initialFilter,
  filterButtons: (state) => state.filterButtons,
};

const actions = {
  saveDiscountRule: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "saveDiscountRule", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.save,
      payload.discountRule,
      function (response, error) {
        commit("setLoadingStatus", { api: "saveDiscountRule", status: false });
        if (response) {
          commit(
            "setMessage",
            { text: "Saved", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  deleteDiscountRule: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "deleteDiscountRule", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.delete,
      payload.discountRule,
      function (response, error) {
        commit("setLoadingStatus", { api: "deleteDiscountRule", status: false });
        if (response) {
          commit(
            "setMessage",
            { text: "Deleted", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
        }
      }
    );
  },

  getDiscountRuleById: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "getDiscountRuleById", status: true });
    HttpService.get(
      Apis.backendBaseUrl + Apis.discountRule.getById + "?id=" + payload._id,
      function (response, error) {
        commit("setLoadingStatus", { api: "getDiscountRuleById", status: false });
        if (response) {
          commit("setDiscountRule", response.data.body);
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          console.log(error);
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  toggleActive: ({ commit }, payload) => {
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.toggleActive,
      { _id: payload._id },
      function (response, error) {
        if (response) {
          commit(
            "setMessage",
            { text: "Status updated", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
        }
      }
    );
  },

  duplicateRule: ({ commit }, payload) => {
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.duplicate,
      { _id: payload._id },
      function (response, error) {
        if (response) {
          commit(
            "setMessage",
            { text: "Rule duplicated", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
        }
      }
    );
  },

  previewOrder: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "previewOrder", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.previewOrder,
      { orderCode: payload.orderCode, customerId: payload.customerId, couponCodes: payload.couponCodes, employeePIN: payload.employeePIN, instantPercentOff: payload.instantPercentOff, instantDollarOff: payload.instantDollarOff, reason: payload.reason },
      function (response, error) {
        commit("setLoadingStatus", { api: "previewOrder", status: false });
        if (response) {
          commit("setPreviewResult", response.data.body);
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  previewDiscount: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "previewDiscount", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.previewDiscount,
      {
        orderCode: payload.orderCode,
        customerId: payload.customerId,
        rule: payload.rule
      },
      function (response, error) {
        commit("setLoadingStatus", { api: "previewDiscount", status: false });
        if (response) {
          commit("setPreviewResult", response.data.body);
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },

  setSelectedFilter: ({ commit }, payload) => {
    commit("setSelectedFilter", payload);
  },
  setSelectedPreFilters: ({ commit }, payload) => {
    commit("setSelectedPreFilters", payload);
  },
  updateSort: ({ commit }, payload) => {
    commit("updateSort", payload);
  },
  updatePagination: ({ commit }, payload) => {
    commit("updatePagination", payload);
  },
  updateInitialFilter: ({ commit }, payload) => {
    commit("updateInitialFilter", payload);
  },
  clearPreviewResult: ({ commit }) => {
    commit("setPreviewResult", null);
  },
  updateFilterButtons: ({ commit }, payload) => {
    commit("updateFilterButtons", payload);
  },

  resetUsages: ({ commit }, payload) => {
    commit("setLoadingStatus", { api: "resetUsages", status: true });
    HttpService.post(
      Apis.backendBaseUrl + Apis.discountRule.resetUsages,
      { discountRuleId: payload.discountRuleId },
      function (response, error) {
        commit("setLoadingStatus", { api: "resetUsages", status: false });
        if (response) {
          commit(
            "setMessage",
            { text: "Usages reset successfully", type: "success" },
            { root: true }
          );
          if (payload.positiveCallback) {
            payload.positiveCallback(response.data.body);
          }
        } else {
          console.log(error);
          commit(
            "setMessage",
            { text: error.message, type: "error" },
            { root: true }
          );
          if (payload.negativeCallback) {
            payload.negativeCallback(error);
          }
        }
      }
    );
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};

